import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTooltipSeen } from '../../hooks/useTooltipSeen';
import './ContextualTooltip.css';

/**
 * Contextual Tooltip for feature discovery.
 * Shows once per feature, dismisses on tap anywhere.
 *
 * @param {Object} props
 * @param {string} props.id - Unique tooltip ID for localStorage tracking
 * @param {string} props.text - Tooltip message text
 * @param {React.ReactNode} [props.icon] - Optional icon to display
 * @param {React.RefObject} props.anchorRef - Reference to the anchor element
 * @param {'top'|'bottom'|'left'|'right'} [props.position='bottom'] - Position relative to anchor
 * @param {boolean} [props.show=true] - Whether tooltip should be shown (if not already seen)
 * @param {number} [props.delay=300] - Delay before showing tooltip (ms)
 * @param {boolean} [props.showDismissHint=false] - Show "Tap to dismiss" hint
 * @param {Function} [props.onDismiss] - Callback when tooltip is dismissed
 */
function ContextualTooltip({
  id,
  text,
  icon,
  anchorRef,
  position = 'bottom',
  show = true,
  delay = 300,
  showDismissHint = false,
  onDismiss,
}) {
  const { hasSeen, markSeen } = useTooltipSeen(id);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);

  // Calculate position based on anchor element
  const updatePosition = useCallback(() => {
    if (!anchorRef?.current || !tooltipRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 8;

    let top, left;

    switch (position) {
      case 'top':
        top = anchorRect.top - tooltipRect.height - padding;
        left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'bottom':
        top = anchorRect.bottom + padding;
        left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'left':
        top = anchorRect.top + (anchorRect.height / 2) - (tooltipRect.height / 2);
        left = anchorRect.left - tooltipRect.width - padding;
        break;
      case 'right':
        top = anchorRect.top + (anchorRect.height / 2) - (tooltipRect.height / 2);
        left = anchorRect.right + padding;
        break;
      default:
        top = anchorRect.bottom + padding;
        left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
    }

    // Keep tooltip within viewport
    const viewportPadding = 16;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));
    top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipRect.height - viewportPadding));

    setCoords({ top, left });
  }, [anchorRef, position]);

  // Show tooltip after delay if not already seen
  useEffect(() => {
    if (hasSeen || !show || !anchorRef?.current) {
      setIsVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [hasSeen, show, delay, anchorRef]);

  // Update position when visible
  useEffect(() => {
    if (!isVisible) return;

    // Initial position update
    const frame = requestAnimationFrame(updatePosition);

    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, updatePosition]);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      markSeen();
      setIsVisible(false);
      setIsExiting(false);
      onDismiss?.();
    }, 150); // Match exit animation duration
  }, [markSeen, onDismiss]);

  if (!isVisible || hasSeen) {
    return null;
  }

  return createPortal(
    <>
      <div
        className="contextual-tooltip-backdrop"
        onClick={handleDismiss}
        aria-hidden="true"
      />
      <div
        ref={tooltipRef}
        className={`contextual-tooltip ${icon ? 'has-icon' : ''} ${isExiting ? 'exiting' : ''}`}
        data-position={position}
        style={{ top: coords.top, left: coords.left }}
        role="tooltip"
        aria-live="polite"
      >
        {icon && (
          <span className="contextual-tooltip-icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <div>
          <p className="contextual-tooltip-text">{text}</p>
          {showDismissHint && (
            <p className="contextual-tooltip-dismiss-hint">Tap to dismiss</p>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

export default ContextualTooltip;
